const normalizeRecordId = (value = '') =>
  String(value || '').trim().toUpperCase().replace(/[^A-Z0-9]/g, '');

const CHAT_STORAGE_PREFIX = 'genpact.agentAssist.chat';
const FNOL_GENERATED_STORAGE_KEY = 'genpact.agentAssist.latestFnolGenerated';
const CLAIM_ID_PATTERN = /\b(C-\d{6}-\d{3,})\b/i;
const FNOL_ID_PATTERN = /\b(F-\d{6}-\d{3,})\b/i;

const deriveFnolFromClaim = (value = '') => {
  const claimId = String(value || '').trim().toUpperCase();
  return claimId.startsWith('C-') ? `F-${claimId.slice(2)}` : '';
};

const deriveClaimFromFnol = (value = '') => {
  const fnolId = String(value || '').trim().toUpperCase();
  return fnolId.startsWith('F-') ? `C-${fnolId.slice(2)}` : '';
};

const collectRecordKeys = (ids = []) => {
  const keys = new Set();

  ids.forEach((id) => {
    const value = String(id || '').trim().toUpperCase();
    if (!value) return;

    keys.add(normalizeRecordId(value));
    if (value.startsWith('C-')) keys.add(normalizeRecordId(deriveFnolFromClaim(value)));
    if (value.startsWith('F-')) keys.add(normalizeRecordId(deriveClaimFromFnol(value)));
  });

  return keys;
};

const messageMatchesRecord = (message = {}, recordKeys) => {
  const ids = [
    message.automationIds?.claimId,
    message.automationIds?.fnolId,
    ...(String(message.text || '').match(new RegExp(CLAIM_ID_PATTERN, 'gi')) || []),
    ...(String(message.text || '').match(new RegExp(FNOL_ID_PATTERN, 'gi')) || []),
  ];

  return ids.some((id) => recordKeys.has(normalizeRecordId(id)));
};

const normalizeStoredSender = (sender = '') => {
  if (sender === 'user') return 'customer';
  if (sender === 'bot') return 'agent';
  return sender || 'agent';
};

const parseStoredJson = (key) => {
  try {
    return JSON.parse(window.localStorage.getItem(key) || 'null');
  } catch {
    return null;
  }
};

const buildTranscriptFromStoredChat = (stored = {}) => {
  const messages = Array.isArray(stored?.messages) ? stored.messages : [];
  const visibleMessages = messages
    .filter((message) => String(message?.text || '').trim())
    .map((message) => ({
      sender: normalizeStoredSender(message.sender),
      text: message.text,
    }));

  if (!visibleMessages.length) return null;

  return {
    type: 'chat',
    title: 'Web Chat Transcript',
    session_id: stored.sessionId || '',
    timestamp: stored.savedAt || '',
    messages: visibleMessages,
  };
};

const getLatestGeneratedChatTranscript = (recordKeys) => {
  const generated = parseStoredJson(FNOL_GENERATED_STORAGE_KEY);
  if (!generated) return null;

  const generatedIds = [generated.fnolId, generated.claimId].map(normalizeRecordId).filter(Boolean);
  const matchesRequestedRecord = generatedIds.some((id) => recordKeys.has(id));
  if (!matchesRequestedRecord) return null;

  if (generated.chatStorageKey?.startsWith(CHAT_STORAGE_PREFIX)) {
    const linkedTranscript = buildTranscriptFromStoredChat(parseStoredJson(generated.chatStorageKey));
    if (linkedTranscript) return linkedTranscript;
  }

  let newestTranscript = null;
  let newestTime = 0;
  for (let index = 0; index < window.localStorage.length; index += 1) {
    const key = window.localStorage.key(index);
    if (!key?.startsWith(CHAT_STORAGE_PREFIX)) continue;

    const stored = parseStoredJson(key);
    const transcript = buildTranscriptFromStoredChat(stored);
    if (!transcript) continue;

    const savedTime = new Date(stored?.savedAt || 0).getTime() || index;
    if (savedTime >= newestTime) {
      newestTime = savedTime;
      newestTranscript = transcript;
    }
  }

  return newestTranscript;
};

const getStoredChatTranscript = (...ids) => {
  if (typeof window === 'undefined' || !window.localStorage) return null;

  const recordKeys = collectRecordKeys(ids);
  if (!recordKeys.size) return null;

  for (let index = 0; index < window.localStorage.length; index += 1) {
    const key = window.localStorage.key(index);
    if (!key?.startsWith(CHAT_STORAGE_PREFIX)) continue;

    try {
      const stored = parseStoredJson(key);
      const messages = Array.isArray(stored?.messages) ? stored.messages : [];
      if (!messages.some((message) => messageMatchesRecord(message, recordKeys))) continue;

      return buildTranscriptFromStoredChat(stored);
    } catch {
      // Ignore unrelated or malformed local storage entries.
    }
  }

  return getLatestGeneratedChatTranscript(recordKeys);
};

const chatTranscriptsByRecordId = {
  C260803002: {
    type: 'chat',
    title: 'Web Chat Transcript',
    timestamp: 'Aug 3, 2026 - 4:34 PM',
    messages: [
      {
        sender: 'customer',
        text: 'OK',
      },
      {
        sender: 'system',
        text: 'Handoff to Universal Closing Agent (via 1 agent)',
      },
      {
        sender: 'system',
        text: '13 Steps completed in 18.648s',
      },
      {
        sender: 'agent',
        text: "Thanks for your patience. I'm submitting your report now.",
      },
      {
        sender: 'agent',
        text:
          'Your claim has been successfully submitted. Your FNOL reference number is F-260803-002. Your claim number is C-260803-002. An adjuster will review the information and contact you regarding next steps. You may upload photos, receipts, estimates, reports and supporting documents through the claims portal or mobile application. Is 9876543210 still the best phone number for follow-up?',
      },
    ],
  },
};

chatTranscriptsByRecordId.F260803002 = chatTranscriptsByRecordId.C260803002;

export const getFnolTranscript = (...ids) => {
  const storedTranscript = getStoredChatTranscript(...ids);
  if (storedTranscript) return storedTranscript;

  const matchedId = ids.map(normalizeRecordId).find((id) => chatTranscriptsByRecordId[id]);
  return matchedId ? chatTranscriptsByRecordId[matchedId] : null;
};
